import React from 'react';
import { useHistory } from 'react-router-dom';
import { useTypedSelector } from 'src/redux/utils';
import ConnectWalletModal from './ConnectWalletModal';
import TransactionConfirmModal from './TransactionConfirmModal';
import TransactionDetailModal from './TransactionDetailModal';
import { RouteName } from 'src/routes/config';
import { navigateToMarketPlacePage } from 'src/utils/navigate';
import { MarketPlaceTab } from 'src/Pages/MarketplaceV2/Components/MarketTab/configs';

const noop = () => {};

const CheckoutContext = /*#__PURE__*/React.createContext({
  // These are default callbacks if hook is used without provider.
  startCheckout: () => Promise.resolve(),
  openModal: noop,
  closeModal: noop
});

const CheckoutProvider = ({
  children
}) => {
  const history = useHistory();
  const [cosmetic, setCosmetic] = React.useState(null);
  const [modal, setModal] = React.useState(null);
  const [successCosmetic, setSuccessCosmetic] = React.useState();
  const startCheckout = React.useCallback(cosmetic => {
    setCosmetic(cosmetic);
    setModal('connect_wallet');
  }, []);
  const openModal = React.useCallback(modal => {
    setModal(modal);
  }, []);
  const closeModal = React.useCallback(() => {
    setCosmetic(null);
    setModal(null);
  }, []);
  const context = React.useMemo(() => {
    return {
      cosmetic: cosmetic || undefined,
      modal: modal || undefined,
      startCheckout,
      openModal,
      closeModal
    };
  }, [cosmetic, modal]); // Auto open the TransactionConfirm if the user is already signed in

  const signedIn = useTypedSelector(state => !!state.userSignIn.accessToken);
  React.useEffect(() => {
    if (signedIn && modal === 'connect_wallet') {
      openModal('transaction_confirm');
    }
  }, [signedIn, modal]); // Render the current modal

  const renderModal = () => {
    switch (modal) {
      case 'connect_wallet':
        return /*#__PURE__*/React.createElement(ConnectWalletModal, {
          onClose: () => {
            closeModal();
          },
          onConnectButtonPressed: () => {
            history.push('/connect', {
              returnBack: cosmetic ? `${RouteName.CosmeticDetail}/${cosmetic.id}` : ''
            });
          }
        });

      case 'transaction_confirm':
        return cosmetic ? /*#__PURE__*/React.createElement(TransactionConfirmModal, {
          cosmetic: cosmetic,
          onCheckoutCompleted: (cosmetic, price) => {
            setSuccessCosmetic({
              cosmetic,
              price
            });
            openModal('transaction_detail');
          },
          onClose: () => {
            closeModal();
          }
        }) : null;

      case 'transaction_detail':
        return /*#__PURE__*/React.createElement(TransactionDetailModal, {
          cosmetic: successCosmetic === null || successCosmetic === void 0 ? void 0 : successCosmetic.cosmetic,
          balance: successCosmetic === null || successCosmetic === void 0 ? void 0 : successCosmetic.price,
          onClose: () => {
            closeModal();
            navigateToMarketPlacePage({
              tab: MarketPlaceTab.COSMETICS
            });
          }
        });

      default:
        return null;
    }
  };

  return /*#__PURE__*/React.createElement(CheckoutContext.Provider, {
    value: context
  }, children, renderModal());
};

export const useCheckout = () => {
  const {
    startCheckout
  } = React.useContext(CheckoutContext);
  return {
    startCheckout
  };
};
export default CheckoutProvider;